import { z } from 'zod';

export const CompanionProfileSchema = z.object({
  id: z.string(),
  world_id: z.string(),
  name: z.string(),
  user_preferred_name: z.string().nullable().optional(),
  pronouns: z.string().nullable().optional(),
  apparent_age: z.string().nullable().optional(),
  role: z.string().nullable().optional(),
  relationship_stance: z.string().nullable().optional(),
  communication_medium: z.enum([
    'text_message', 'chat_app', 'social_dm', 'voice_call', 'video_call', 'in_person', 'letters', 'journal'
  ]).nullable().optional(),
  speech_patterns: z.string().nullable().optional(),
  vocabulary_tier: z.string().nullable().optional(),
  emotional_baseline: z.string().nullable().optional(),
  primary_drive: z.string().nullable().optional(),
  attachment_style: z.string().nullable().optional(),
  communication_style: z.string().nullable().optional(),
  core_wound: z.string().nullable().optional(),
  defense_mechanisms: z.array(z.string()).nullable().optional(),
  blind_spots: z.array(z.string()).nullable().optional(),
  hidden_desires: z.array(z.string()).nullable().optional(),
  fears: z.array(z.string()).nullable().optional(),
  contradictions: z.array(z.string()).nullable().optional(),
  formative_experiences: z.array(z.string()).nullable().optional(),
  personal_values: z.array(z.string()).nullable().optional(),
  quirks: z.array(z.string()).nullable().optional(),
  inner_world: z.string().nullable().optional(),
  backstory: z.string().nullable().optional(),
  current_life_situation: z.string().nullable().optional(),
  occupation: z.string().nullable().optional(),
  interests: z.array(z.string()).nullable().optional(),
  user_description: z.string().nullable().optional(),
  boundaries_json: z.string().nullable().optional(),
  acknowledge_ai: z.union([z.boolean(), z.number()]).optional(),
  provider: z.string().nullable().optional(),
  model: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});

export type CompanionProfile = z.infer<typeof CompanionProfileSchema>;

export const CompanionMemorySchema = z.object({
  id: z.string(),
  world_id: z.string(),
  type: z.string(),
  key: z.string(),
  value: z.string(),
  confidence: z.number().nullable().optional(),
  source_message_id: z.string().nullable().optional(),
  is_user_confirmed: z.union([z.boolean(), z.number()]).optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});

export type CompanionMemory = z.infer<typeof CompanionMemorySchema>;

export const CompanionContactSchema = z.object({
  id: z.string(),
  world_id: z.string(),
  name: z.string(),
  relation: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  sentiment: z.string().nullable().optional(),
  last_mentioned_at: z.string().nullable().optional(),
  mention_count: z.number().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});

export type CompanionContact = z.infer<typeof CompanionContactSchema>;

export const CompanionSummarySchema = z.object({
  id: z.string(),
  world_id: z.string(),
  rolling_summary: z.string(),
  relationship_phase: z.string().nullable().optional(),
  last_significant_event: z.string().nullable().optional(),
  last_message_id: z.string().nullable().optional(),
  message_count_at_summary: z.number().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});

export type CompanionSummary = z.infer<typeof CompanionSummarySchema>;

// Lightweight state for status bar (from world_state payload)
export const CompanionStateSchema = z.object({
  mood: z.string().nullable(),
  moodIntensity: z.number().nullable(),
  relationshipPhase: z.string().nullable(),
  intimacyLevel: z.number().nullable(),
});

export type CompanionState = z.infer<typeof CompanionStateSchema>;
